interface HubSpotForm {
  forms: {
    create: (opts: Record<string, unknown>) => void;
  };
}

declare global {
  interface Window {
    hbspt?: HubSpotForm;
  }
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Stops the cursor handler firing while the cursor is still travelling away from a nav "Book a demo" link.
const EXIT_INTENT_ARM_DELAY_MS = 3000;

type ExitModalTrigger = 'exit_intent' | 'time' | 'scroll';

// page_title (not page_name) to match every other tracking listener, so these
// events join to the global book_a_demo_submit / form_submission on one key.
const trackPageName = (): string => window.InteractTracking?.getContext().page_title ?? '';

function initModal(wrap: HTMLElement): void {
  const portalId = wrap.dataset.portalId ?? '';
  const formId = wrap.dataset.formId ?? '';
  const region = wrap.dataset.region ?? 'na1';
  const sfdcCampaignId = wrap.dataset.sfdcCampaignId ?? '';
  const enableExitIntent = wrap.dataset.enableExitIntent === 'true';
  const exitIntentThreshold = Number(wrap.dataset.exitIntentThreshold ?? 140);
  const enableTimeTrigger = wrap.dataset.enableTimeTrigger === 'true';
  const timeDelay = Number(wrap.dataset.timeDelay ?? 30);
  const minScrollPercent = Number(wrap.dataset.minScrollPercent ?? 45);
  const showOnce = wrap.dataset.showOnce === 'true';

  if (!portalId || !formId) {
    return;
  }

  const storageKey = `interact-exit-modal-shown-${formId}`;

  const panel = wrap.querySelector<HTMLElement>('.interact-exit-modal__panel');
  const formTarget = wrap.querySelector<HTMLElement>('.interact-exit-modal__form');
  if (!panel || !formTarget) {
    return;
  }

  let formInitialised = false;
  let opened = false;
  let hasShown = showOnce && sessionStorage.getItem(storageKey) === '1';
  let lastFocusedElement: HTMLElement | null = null;

  let exitIntentHandler: ((e: MouseEvent) => void) | null = null;
  let exitIntentArmTimer: number | null = null;
  let timeTimer: number | null = null;
  let scrollHandler: (() => void) | null = null;
  let inputHandler: ((e: Event) => void) | null = null;

  let timeElapsedReady = timeDelay <= 0;
  let scrollReady = minScrollPercent <= 0;
  let formStarted = false;
  let modalEngaged = false;
  let modalFormFocused = false;
  let modalSubmitted = false;

  const removeTriggers = (): void => {
    if (exitIntentArmTimer !== null) {
      clearTimeout(exitIntentArmTimer);
      exitIntentArmTimer = null;
    }
    if (exitIntentHandler) {
      document.removeEventListener('mousemove', exitIntentHandler);
      exitIntentHandler = null;
    }
    if (timeTimer !== null) {
      clearTimeout(timeTimer);
      timeTimer = null;
    }
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
    if (inputHandler) {
      document.removeEventListener('input', inputHandler);
      inputHandler = null;
    }
  };

  const initHubSpotForm = (): void => {
    if (formInitialised) {
      return;
    }
    const opts: Record<string, unknown> = {
      portalId,
      formId,
      region,
      target: `#${formTarget.id}`,
    };
    if (sfdcCampaignId) {
      opts.sfdcCampaignId = sfdcCampaignId;
    }
    if (window.hbspt && window.hbspt.forms && typeof window.hbspt.forms.create === 'function') {
      window.hbspt.forms.create(opts);
      formInitialised = true;
    }
  };

  // Exit-intent has no gates — the cursor leaving IS the signal. Gating it on formStarted would let stray autofill/mount events silently kill it.
  const autoShowGatesPass = (): boolean => timeElapsedReady && scrollReady && !formStarted;

  const open = (trigger: ExitModalTrigger): void => {
    if (opened || hasShown) return;
    opened = true;
    hasShown = true;
    removeTriggers();

    window.InteractTracking?.track('exit_modal_shown', {
      page_name: trackPageName(),
      trigger,
    });

    if (showOnce) {
      sessionStorage.setItem(storageKey, '1');
    }

    lastFocusedElement = document.activeElement as HTMLElement | null;

    wrap.removeAttribute('hidden');
    wrap.setAttribute('aria-hidden', 'false');
    document.body.classList.add('interact-exit-modal-open');
    // Defer .is-open by a frame so the CSS transition catches the change.
    requestAnimationFrame(() => {
      wrap.classList.add('is-open');
      panel.focus();
    });

    initHubSpotForm();
  };

  const close = (method: 'close_button' | 'backdrop' | 'escape'): void => {
    if (!opened) return;
    opened = false;

    // A converted session already emitted exit_modal_submit; closing the
    // thank-you screen is not an abandonment, so don't also count a dismissal.
    if (!modalSubmitted) {
      window.InteractTracking?.track('exit_modal_dismissed', {
        page_name: trackPageName(),
        method,
        engaged: modalEngaged,
      });
    }

    wrap.classList.remove('is-open');
    wrap.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('interact-exit-modal-open');

    // Match the 0.3s fade-out before re-hiding from assistive tech.
    setTimeout(() => {
      if (!opened) {
        wrap.setAttribute('hidden', '');
      }
    }, 350);

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  };

  const tryAutoOpen = (trigger: ExitModalTrigger): void => {
    if (!enableTimeTrigger) return;
    if (!autoShowGatesPass()) return;
    open(trigger);
  };

  if (hasShown) {
    return;
  }

  if (timeDelay > 0) {
    timeTimer = window.setTimeout(() => {
      timeTimer = null;
      timeElapsedReady = true;
      tryAutoOpen('time');
    }, timeDelay * 1000);
  }

  if (minScrollPercent > 0) {
    const computeScrollPct = (): number => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return 100;
      return (window.scrollY / max) * 100;
    };
    scrollHandler = (): void => {
      if (scrollReady) return;
      if (computeScrollPct() >= minScrollPercent) {
        scrollReady = true;
        if (scrollHandler) {
          window.removeEventListener('scroll', scrollHandler);
          scrollHandler = null;
        }
        tryAutoOpen('scroll');
      }
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });
    // Re-check after `load` so lazy images / web fonts can finish growing scrollHeight.
    if (document.readyState === 'complete') {
      scrollHandler();
    } else {
      window.addEventListener('load', () => scrollHandler?.(), { once: true });
    }
  }

  // isTrusted filters out autofill / programmatic value-sets that would falsely flip the gate.
  inputHandler = (e: Event): void => {
    if (formStarted) return;
    if (!e.isTrusted) return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.hs-form')) {
      formStarted = true;
    }
  };
  document.addEventListener('input', inputHandler);

  if (enableExitIntent) {
    exitIntentArmTimer = window.setTimeout(() => {
      exitIntentArmTimer = null;
      exitIntentHandler = (e: MouseEvent) => {
        if (e.clientY <= exitIntentThreshold) {
          open('exit_intent');
        }
      };
      document.addEventListener('mousemove', exitIntentHandler);
    }, EXIT_INTENT_ARM_DELAY_MS);
  }

  wrap.querySelectorAll<HTMLElement>('[data-exit-modal-close]').forEach((el) => {
    const method = el.classList.contains('interact-exit-modal__backdrop')
      ? 'backdrop'
      : 'close_button';
    el.addEventListener('click', () => close(method));
  });

  document.addEventListener('keydown', (e) => {
    if (!opened) return;
    if (e.key === 'Escape') {
      close('escape');
    } else if (e.key === 'Tab') {
      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;
      // While HubSpot is still mounting there's only the close button; pin Tab to it.
      if (focusables.length === 1) {
        e.preventDefault();
        focusables[0].focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Any focus inside the modal's own form scopes conversion attribution to it —
  // covers typing and pre-filled one-click submits by known HubSpot contacts.
  formTarget.addEventListener('focusin', () => {
    modalFormFocused = true;
  });

  // Engagement: first real keystroke inside the modal's form, while it is open.
  formTarget.addEventListener('input', (e) => {
    if (modalEngaged || !opened || !e.isTrusted) return;
    modalEngaged = true;
    window.InteractTracking?.track('exit_modal_engaged', {
      page_name: trackPageName(),
    });
  });

  // Conversion: HubSpot's submit message carries no DOM origin, and the same
  // book-a-demo form is often embedded on the page too — so count a submit only
  // when the modal is open and its own form was the one interacted with.
  const onFormSubmitted = (e: MessageEvent): void => {
    if (modalSubmitted || !opened || !modalFormFocused) return;
    const data = e.data as {
      type?: string;
      eventName?: string;
      id?: string;
      data?: { formGuid?: string };
    } | null;
    if (!data || data.type !== 'hsFormCallback' || data.eventName !== 'onFormSubmitted') return;
    if ((data.id || data.data?.formGuid || '') !== formId) return;
    modalSubmitted = true;
    window.InteractTracking?.track('exit_modal_submit', {
      page_name: trackPageName(),
      form_id: formId,
    });
  };
  window.addEventListener('message', onFormSubmitted);

  window.addEventListener(
    'pagehide',
    () => {
      removeTriggers();
      window.removeEventListener('message', onFormSubmitted);
    },
    { once: true },
  );
}

function bootstrap(): void {
  document.querySelectorAll<HTMLElement>('.interact-exit-modal').forEach(initModal);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
